import { useMemo, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import LoadingScreen from "@components/content/LoadingScreen";
import ConfirmModal from "@components/content/ConfirmModal";
import { Button, Input, Table } from "reactstrap";
import { ProviderRecibeRepairApi } from "@/api/ProviderRecibeRepairApi";
import {
  formatRepairDocumentsSummary,
  RepairPlan,
  RepairPlanStatus,
} from "@utils/repairProviderRecibeLinks";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import cx from "classnames";

const STATUS_LABELS: Record<RepairPlanStatus, string> = {
  ready: "Listo",
  skipped: "Omitido",
  conflict: "Conflicto",
  error: "Error",
};

const ProviderRecibeRepairIndex = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState<RepairPlan[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);

  const readyPlans = useMemo(
    () => plans.filter((plan) => plan.status === "ready"),
    [plans],
  );

  const scanCandidates = async () => {
    setIsLoading(true);
    try {
      const candidates = await ProviderRecibeRepairApi.scanRepairCandidates();
      setPlans(candidates);
      setSelectedIds(candidates.filter((plan) => plan.status === "ready").map((plan) => plan.recibeId));
      setHasScanned(true);
      if (candidates.length === 0) {
        showAlert("success", "No se encontraron recibos incompletos para reparar");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "No fue posible escanear recibos";
      showAlert("danger", message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelection = (recibeId: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        return current.includes(recibeId) ? current : [...current, recibeId];
      }
      return current.filter((id) => id !== recibeId);
    });
  };

  const toggleSelectAllReady = (checked: boolean) => {
    if (checked) {
      setSelectedIds(readyPlans.map((plan) => plan.recibeId));
      return;
    }
    setSelectedIds([]);
  };

  const runRepair = async () => {
    if (selectedIds.length === 0) {
      showAlert("danger", "Debe seleccionar al menos un recibo listo para reparar");
      return;
    }

    setIsLoading(true);
    try {
      const summary = await ProviderRecibeRepairApi.repairProviderRecibes(selectedIds);
      const messages = [
        `Reparados: ${summary.repaired.length}`,
        `Omitidos: ${summary.skipped.length}`,
        `Fallidos: ${summary.failed.length}`,
      ];

      if (summary.failed.length > 0) {
        showAlert("warning", messages.join(". "));
      } else {
        showAlert("success", messages.join(". "));
      }

      await scanCandidates();
    } catch (err) {
      const message = err instanceof Error ? err.message : "No fue posible reparar los recibos";
      showAlert("danger", message);
    } finally {
      setIsLoading(false);
      setIsConfirmOpen(false);
    }
  };

  return (
    <PageContainer
      title="Reparar Recibos de Proveedor"
      backUrl="/user/provider-recibes"
    >
      <p className="mb-4">
        Esta herramienta detecta recibos sin proveedor o sin facturas ligadas, usando el campo motivo
        para reconstruir los vínculos con FEC y facturas normales de proveedor.
      </p>

      <div className={styles.actions}>
        <Button color="primary" onClick={scanCandidates} disabled={isLoading}>
          Escanear
        </Button>
        <Button
          color="success"
          disabled={isLoading || selectedIds.length === 0}
          onClick={() => setIsConfirmOpen(true)}
        >
          Reparar seleccionados ({selectedIds.length})
        </Button>
      </div>

      {hasScanned && (
        <div className="table-responsive mt-4">
          <Table hover bordered className={styles.table}>
            <thead>
              <tr>
                <th>
                  <Input
                    type="checkbox"
                    checked={readyPlans.length > 0 && selectedIds.length === readyPlans.length}
                    onChange={(event) => toggleSelectAllReady(event.target.checked)}
                    aria-label="Seleccionar todos los recibos listos"
                  />
                </th>
                <th>N.º Recibo</th>
                <th>Motivo</th>
                <th>Proveedor propuesto</th>
                <th>Facturas</th>
                <th>Estado</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center">
                    No hay recibos incompletos para reparar
                  </td>
                </tr>
              )}
              {plans.map((plan) => (
                <tr key={plan.recibeId}>
                  <td>
                    <Input
                      type="checkbox"
                      checked={selectedIds.includes(plan.recibeId)}
                      disabled={plan.status !== "ready"}
                      onChange={(event) => toggleSelection(plan.recibeId, event.target.checked)}
                      aria-label={`Seleccionar recibo ${plan.numeroRecibo ?? plan.recibeId}`}
                    />
                  </td>
                  <td>{plan.numeroRecibo ?? "-"}</td>
                  <td>{plan.motivo || "-"}</td>
                  <td>{plan.proposedProviderName || plan.proposedProviderId || "-"}</td>
                  <td>{formatRepairDocumentsSummary(plan.documents) || "-"}</td>
                  <td>
                    <span
                      className={cx(styles.statusBadge, styles[`status_${plan.status}`])}
                    >
                      {STATUS_LABELS[plan.status]}
                    </span>
                  </td>
                  <td>{plan.message}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        toggle={() => setIsConfirmOpen(false)}
        title="Confirmar reparación"
        primaryBtn="Reparar"
        primaryBtnColor="success"
        onConfirm={runRepair}
      >
        ¿Desea reparar {selectedIds.length} recibo(s) seleccionado(s)?
      </ConfirmModal>

      <LoadingScreen isLoading={isLoading} />
    </PageContainer>
  );
};

export default ProviderRecibeRepairIndex;
